import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Typography, Row, Col, Statistic, DatePicker, Space, Select, Spin, Alert, Tag, Input } from 'antd';
import Table from 'components/Common/Table';
import { ClockCircleOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';
import axios from 'axios';
import useIsMobile from '../../hooks/useIsMobile';
import ReportFilterModal, { DatePresetButtons, MobileFilterButton, getDateRangeFromPreset } from '../../components/Reports/ReportFilterModal';
import ExportPdfButton from '../../components/Reports/ExportPdfButton';
import useUrlFilters from '../../hooks/useUrlFilters';

const { RangePicker } = DatePicker;

const { Title, Text } = Typography;
const { Option } = Select;

const AGING_COLORS = {
  'Current': '#52c41a',
  '1-30 Days': '#faad14',
  '31-60 Days': '#fa8c16',
  '61-90 Days': '#f5222d',
  '90+ Days': '#cf1322',
};

// Filters live in the URL so a filtered report can be shared as a link.
// Relative presets are kept as the preset name; only custom and FY ranges carry dates.
const URL_FILTERS = {
  preset: { type: 'string', default: 'all' },
  range: { type: 'dateRange', keys: ['start_date', 'end_date'] },
  customer: { type: 'string', default: 'all' },
  search: { type: 'string' },
};
const PRESETS_WITH_DATES = ['custom', 'this_fy', 'last_fy'];
// Relative presets are recomputed from the name; a custom range with no dates is empty.
const resolveRange = (preset, range) =>
  (PRESETS_WITH_DATES.includes(preset) && range) || (preset === 'custom' ? null : getDateRangeFromPreset(preset));

const AccountsReceivableReport = () => {
  const isMobile = useIsMobile();
  const reportRef = useRef(null);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const filterPreset = filters.preset;
  const dateRange = useMemo(() => resolveRange(filterPreset, filters.range), [filterPreset, filters.range]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const customerFilter = filters.customer;
  const setCustomerFilter = (value) => setFilters({ customer: value });
  const searchText = filters.search || '';
  const setSearchText = (value) => setFilters({ search: value });
  const [searchInput, setSearchInput] = useState(searchText);
  useEffect(() => setSearchInput(searchText), [searchText]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const useDateRange = filterPreset === 'custom' || filterPreset === 'this_fy' || filterPreset === 'last_fy';
      const params = {
        filter_preset: useDateRange ? undefined : (filterPreset === 'all' ? 'all_time' : filterPreset),
        start_date: useDateRange && dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
        end_date: useDateRange && dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
      };

      if (customerFilter !== 'all') {
        params.customer_id = customerFilter;
      }

      if (searchText.trim()) {
        params.search = searchText.trim();
      }

      const response = await axios.get('/invoices/invoices/receivables-analytics/', { params });

      if (response.status === 200) {
        setData(response.data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error || 'Failed to fetch receivables data');
    } finally {
      setLoading(false);
    }
  }, [filterPreset, dateRange, customerFilter, searchText]);

  // Handle preset change - simplified using helper
  const handlePresetChange = (preset) => {
    setFilters({
      preset,
      range: PRESETS_WITH_DATES.includes(preset) ? getDateRangeFromPreset(preset) : null,
    });
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setFilters({ preset: 'custom', range: [dates[0], dates[1]] });
    } else {
      setFilters({ preset: 'all', range: null });
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({ preset: 'all', range: null, customer: 'all', search: null });
    setSearchInput('');
  };

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filterPreset !== 'all') count++;
    if (customerFilter !== 'all') count++;
    if (searchText.trim()) count++;
    return count;
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    {
      title: 'Invoice #',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      sorter: (a, b) => (a.invoice_number || '').localeCompare(b.invoice_number || ''),
      fixed: isMobile ? false : 'left',
      width: 110,
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      sorter: (a, b) => a.customer.localeCompare(b.customer),
      ellipsis: true,
      width: 150,
    },
    {
      title: 'Invoice Date',
      dataIndex: 'invoice_date',
      key: 'invoice_date',
      sorter: (a, b) => new Date(a.invoice_date) - new Date(b.invoice_date),
      responsive: ['md'],
      width: 100,
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      sorter: (a, b) => new Date(a.due_date) - new Date(b.due_date),
      responsive: ['lg'],
      width: 100,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (val) => `₹${val?.toLocaleString('en-IN')}`,
      sorter: (a, b) => a.total - b.total,
      align: 'right',
      responsive: ['md'],
      width: 100,
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      render: (val) => <Text strong style={{ color: val > 0 ? '#f5222d' : '#52c41a' }}>₹{val?.toLocaleString('en-IN')}</Text>,
      sorter: (a, b) => a.balance - b.balance,
      align: 'right',
      width: 100,
    },
    {
      title: 'Days Overdue',
      dataIndex: 'days_overdue',
      key: 'days_overdue',
      render: (val) => val > 0 ? <Text type="danger">{val} days</Text> : <Text type="success">Current</Text>,
      sorter: (a, b) => a.days_overdue - b.days_overdue,
      align: 'right',
      responsive: ['md'],
      width: 100,
    },
    {
      title: 'Aging',
      dataIndex: 'aging_bucket',
      key: 'aging_bucket',
      render: (bucket) => (
        <Tag color={AGING_COLORS[bucket] || 'default'}>{bucket}</Tag>
      ),
      filters: [
        { text: 'Current', value: 'Current' },
        { text: '1-30 Days', value: '1-30 Days' },
        { text: '31-60 Days', value: '31-60 Days' },
        { text: '61-90 Days', value: '61-90 Days' },
        { text: '90+ Days', value: '90+ Days' },
      ],
      onFilter: (value, record) => record.aging_bucket === value,
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = { SENT: 'blue', PARTIAL: 'orange', READY: 'purple', CANCELLED: 'default' };
        return <Tag color={colors[status] || 'default'} style={status === 'CANCELLED' ? { color: '#6B7280', backgroundColor: '#6B728020' } : {}}>{status}</Tag>;
      },
      responsive: ['lg'],
      width: 90,
    },
  ];

  // Prepare chart data
  const agingChartData = data?.aging_buckets?.map(item => ({
    name: item.bucket,
    amount: item.amount,
    count: item.count,
    fill: AGING_COLORS[item.bucket] || '#1890ff'
  })) || [];

  const pieChartData = data?.aging_buckets?.map(item => ({
    name: item.bucket,
    value: item.amount,
    percentage: item.percentage,
    fill: AGING_COLORS[item.bucket] || '#1890ff'
  })) || [];

  const handleSearch = (value) => {
    setSearchText(value);
  };

  // Render additional filters for modal
  const renderAdditionalFilters = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Customer</Text>
        <Select
          value={customerFilter}
          onChange={setCustomerFilter}
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="children"
        >
          <Option value="all">All Customers</Option>
          {data?.available_customers?.map(customer => (
            <Option key={customer.id} value={customer.id}>{customer.name}</Option>
          ))}
        </Select>
      </div>
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Search Customer</Text>
        <Input.Search
          placeholder="Search customer..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onSearch={handleSearch}
          allowClear
          onClear={() => handleSearch('')}
          style={{ width: '100%' }}
          enterButton
        />
      </div>
    </Space>
  );

  // Tables for the PDF export. Passed explicitly rather than scraped from the
  // rendered tables so the export carries every row, not just the page on screen.
  const pdfTables = [
    {
      title: 'Aging Summary',
      columns: [
        { header: 'Aging Bucket', key: 'bucket' },
        { header: 'Invoices', key: 'count', align: 'right' },
        { header: 'Amount', key: 'amount', align: 'right', format: 'currency' },
        { header: 'Share', key: 'percentage', align: 'right', format: 'percent' },
      ],
      rows: data?.aging_buckets || [],
    },
    {
      title: 'Outstanding by Customer',
      columns: [
        { header: 'Customer', key: 'customer' },
        { header: 'Invoices', key: 'invoices', align: 'right' },
        { header: 'Outstanding', key: 'outstanding', align: 'right', format: 'currency' },
        { header: 'Overdue', key: 'overdue', align: 'right', format: 'currency' },
      ],
      rows: data?.by_customer || [],
    },
    {
      title: 'Outstanding Invoices',
      columns: [
        { header: 'Invoice #', key: 'invoice_number' },
        { header: 'Customer', key: 'customer' },
        { header: 'Invoice Date', key: 'invoice_date' },
        { header: 'Due Date', key: 'due_date' },
        { header: 'Total', key: 'total', align: 'right', format: 'currency' },
        { header: 'Balance', key: 'balance', align: 'right', format: 'currency' },
        { header: 'Days Overdue', key: 'days_overdue', align: 'right', format: (value) => (value > 0 ? `${value} days` : 'Current') },
        { header: 'Aging', key: 'aging_bucket' },
        { header: 'Status', key: 'status' },
      ],
      rows: data?.invoices || [],
    },
  ];

  const pdfMeta = [
    { label: 'Total Outstanding', value: `Rs. ${(data?.summary?.total_outstanding || 0).toLocaleString('en-IN')}` },
    { label: 'Total Overdue', value: `Rs. ${(data?.summary?.total_overdue || 0).toLocaleString('en-IN')}` },
    { label: 'Invoices', value: data?.summary?.total_invoices || 0 },
  ];

  return (
    <div ref={reportRef} style={{
      height: 'calc(100vh - 64px)',
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px 24px',
      backgroundColor: '#f0f2f5'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <Title level={isMobile ? 4 : 2} style={{ margin: 0 }}>Accounts Receivable Report</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <ExportPdfButton
            reportRef={reportRef}
            title="Accounts Receivable Report"
            subtitle={data?.summary?.date_range}
            meta={pdfMeta}
            tables={pdfTables}
            fileName="accounts-receivable-report"
            orientation="landscape"
            disabled={loading || !data}
          />
          {isMobile && (
            <MobileFilterButton
              onClick={() => setFilterModalVisible(true)}
              activeFiltersCount={getActiveFiltersCount()}
            />
          )}
        </div>
      </div>

      {/* Desktop Filters */}
      {!isMobile && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <div>
              <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Date Range:</Text>
              <DatePresetButtons filterPreset={filterPreset} onPresetChange={handlePresetChange} />
            </div>
            <Space wrap size="middle">
              <div>
                <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Custom Date Range:</Text>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="YYYY-MM-DD"
                  size="small"
                  allowClear={true}
                  placeholder={['Start Date', 'End Date']}
                />
              </div>
              <div>
                <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Customer:</Text>
                <Select
                  value={customerFilter}
                  onChange={setCustomerFilter}
                  size="small"
                  style={{ width: 200 }}
                  showSearch
                  optionFilterProp="children"
                >
                  <Option value="all">All Customers</Option>
                  {data?.available_customers?.map(customer => (
                    <Option key={customer.id} value={customer.id}>{customer.name}</Option>
                  ))}
                </Select>
              </div>
            </Space>
          </Space>
        </Card>
      )}

      {/* Mobile Filter Modal */}
      <ReportFilterModal
        visible={filterModalVisible}
        onClose={() => setFilterModalVisible(false)}
        filterPreset={filterPreset}
        dateRange={dateRange}
        onChange={({ preset, range }) => setFilters({
          preset,
          range: PRESETS_WITH_DATES.includes(preset) ? range : null,
        })}
        onClearFilters={handleClearFilters}
        title="Receivables Report Filters"
      >
        {renderAdditionalFilters()}
      </ReportFilterModal>

      {error && (
        <Alert message="Error" description={error} type="error" closable showIcon onClose={() => setError(null)} style={{ marginBottom: '16px' }} />
      )}

      {loading && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}><Text>Loading receivables data...</Text></div>
          </div>
        </Card>
      )}

      {!loading && data && (
        <>
          {/* Summary Statistics */}
          <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>Total Outstanding</span>}
                  value={data.summary?.total_outstanding || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px', color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>Current (Not Due)</span>}
                  value={data.summary?.current || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px', color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>Total Overdue</span>}
                  value={data.summary?.total_overdue || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px', color: '#f5222d' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>Outstanding Invoices</span>}
                  value={data.summary?.total_invoices || 0}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Aging Breakdown Row */}
          <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
            <Col xs={12} sm={8} md={4}>
              <Card size="small" style={{ borderLeft: `3px solid ${AGING_COLORS['1-30 Days']}` }} bodyStyle={{ padding: isMobile ? '10px' : undefined }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '10px' : '14px' }}>1-30 Days</span>}
                  value={data.summary?.overdue_1_30 || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '14px' : '16px', color: AGING_COLORS['1-30 Days'] }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small" style={{ borderLeft: `3px solid ${AGING_COLORS['31-60 Days']}` }} bodyStyle={{ padding: isMobile ? '10px' : undefined }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '10px' : '14px' }}>31-60 Days</span>}
                  value={data.summary?.overdue_31_60 || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '14px' : '16px', color: AGING_COLORS['31-60 Days'] }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small" style={{ borderLeft: `3px solid ${AGING_COLORS['61-90 Days']}` }} bodyStyle={{ padding: isMobile ? '10px' : undefined }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '10px' : '14px' }}>61-90 Days</span>}
                  value={data.summary?.overdue_61_90 || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '14px' : '16px', color: AGING_COLORS['61-90 Days'] }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card size="small" style={{ borderLeft: `3px solid ${AGING_COLORS['90+ Days']}` }} bodyStyle={{ padding: isMobile ? '10px' : undefined }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '10px' : '14px' }}>90+ Days</span>}
                  value={data.summary?.overdue_90_plus || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '14px' : '16px', color: AGING_COLORS['90+ Days'] }}
                />
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
            {/* Aging Bar Chart */}
            <Col xs={24} md={12}>
              <Card size="small" title={<Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Aging Analysis</Text>}>
                {agingChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
                    <BarChart data={agingChartData} margin={{ top: 20, right: isMobile ? 10 : 30, left: isMobile ? 0 : 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: isMobile ? 9 : 11 }} />
                      <YAxis tick={{ fontSize: isMobile ? 9 : 11 }} width={isMobile ? 50 : 60} />
                      <Tooltip formatter={(value) => `₹${value?.toLocaleString('en-IN')}`} />
                      <Bar dataKey="amount" name="Amount">
                        {agingChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}><Text type="secondary">No aging data available</Text></div>
                )}
              </Card>
            </Col>

            {/* Aging Pie Chart */}
            <Col xs={24} md={12}>
              <Card size="small" title={<Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Aging Distribution</Text>}>
                {pieChartData.length > 0 && pieChartData.some(d => d.value > 0) ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
                    <PieChart>
                      <Pie
                        data={pieChartData.filter(d => d.value > 0)}
                        cx="50%"
                        cy="50%"
                        innerRadius={isMobile ? 35 : 50}
                        outerRadius={isMobile ? 65 : 90}
                        paddingAngle={2}
                        dataKey="value"
                        label={isMobile ? false : ({ name, percentage }) => `${name} (${percentage}%)`}
                        labelLine={isMobile ? false : { length: 10, length2: 5 }}
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `₹${value?.toLocaleString('en-IN')}`} />
                      {isMobile && <Legend wrapperStyle={{ fontSize: '11px' }} />}
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}><Text type="secondary">No distribution data available</Text></div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Customer Summary Table */}
          {data.by_customer && data.by_customer.length > 0 && (
            <Card size="small" title={<Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Outstanding by Customer (Top 20)</Text>} style={{ marginBottom: '16px' }}>
              <Table
                dataSource={data.by_customer}
                rowKey="customer"
                size="small"
                pagination={false}
                scroll={{ x: 'max-content' }}
                columns={[
                  { title: 'Customer', dataIndex: 'customer', key: 'customer', ellipsis: true },
                  { title: 'Invoices', dataIndex: 'invoices', key: 'invoices', align: 'right', responsive: ['md'], width: 80 },
                  { title: 'Outstanding', dataIndex: 'outstanding', key: 'outstanding', render: (v) => `₹${v?.toLocaleString('en-IN')}`, align: 'right', width: 110 },
                  { title: 'Overdue', dataIndex: 'overdue', key: 'overdue', render: (v) => <Text type={v > 0 ? 'danger' : 'success'}>₹{v?.toLocaleString('en-IN')}</Text>, align: 'right', width: 100 },
                ]}
              />
            </Card>
          )}

          {/* Invoice Details Table */}
          <Card size="small" title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Outstanding Invoices</Text>
              <Space wrap>
                {!isMobile && (
                  <Input.Search
                    placeholder="Search customer..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onSearch={handleSearch}
                    allowClear
                    onClear={() => handleSearch('')}
                    style={{ width: 250 }}
                    size="small"
                    enterButton
                  />
                )}
                <Text type="secondary" style={{ fontSize: '12px' }}>{data.summary?.total_invoices || 0} invoices</Text>
              </Space>
            </div>
          }>
            <Table
              columns={columns}
              dataSource={data.invoices || []}
              rowKey="id"
              size="small"
              pagination={{
                defaultPageSize: isMobile ? 5 : 10,
                pageSizeOptions: ['5', '10', '20', '50'],
                showSizeChanger: !isMobile,
                showTotal: isMobile ? undefined : (total, range) => `${range[0]}-${range[1]} of ${total}`,
                simple: isMobile,
              }}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </>
      )}

      {!loading && !data && !error && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Text type="secondary">No receivables data available.</Text>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AccountsReceivableReport;
